/**
 * Zustand store — the bridge between the Repository and the UI (ARCHITECTURE
 * §3.3). It orchestrates load/dispatch/recompute but holds no business rules;
 * all derivation (trial balance, statements, tie-out) is done by `core` from
 * this state. Derived values are never stored, so they cannot drift.
 *
 * It is also the single enforcement point for identity & access (Identity
 * mandate): every protected write checks `can(role, action)` before touching the
 * repository, so the rule holds no matter which surface calls it.
 */
import { create } from "zustand";
import type { Account, Currency, Entity, JournalEntry, TaxSettings } from "@core/index";
import { DEFAULT_CHART, ENGINE_NAME, ENGINE_VERSION } from "@core/index";
import {
  ApiRepository,
  IndexedDbRepository,
  buildInitialSettings,
  can,
  hashPassword,
  isAuthRepository,
  newToken,
  newUserId,
  normalizeEmail,
  orgIdFrom,
  sessionValid,
  verifyPassword,
  SESSION_TTL_MS,
  type Action,
  type AppMeta,
  type Repository,
  type Role,
  type Session,
  type User,
} from "@data/index";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

/** "consolidated" is the whole group; otherwise an entity id. */
export type Scope = "consolidated" | string;

/** Organization details captured by the first-run Setup Wizard. */
export interface SetupInput {
  orgName: string;
  baseCurrency: Currency;
  fiscalYearStartMonth: number;
  entityName: string;
  entityKind: Entity["kind"];
  /** First-run owner account created alongside the organization. */
  ownerName: string;
  ownerEmail: string;
  password: string;
}

/** Fields needed to invite a member from the Members & Access screen. */
export interface InviteInput {
  name: string;
  email: string;
  role: Role;
  password: string;
}

export interface AppState {
  engine: { name: string; version: string };
  status: LoadStatus;
  error: string | null;

  entities: Entity[];
  accounts: Account[];
  entries: JournalEntry[];
  settings: TaxSettings | null;
  /** App-level metadata (setup flag + org identity); null until loaded. */
  meta: AppMeta | null;
  /** Organization members (identity mandate). */
  users: User[];
  /** The signed-in local session, or null when signed out. */
  session: Session | null;

  /** Active view scope: consolidated group or a single entity. */
  scope: Scope;

  load: () => Promise<void>;
  setScope: (scope: Scope) => void;
  addEntry: (entry: JournalEntry) => Promise<void>;
  /** Amend a previously posted entry in place (same id); statements re-derive. */
  updateEntry: (entry: JournalEntry) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  saveAccount: (account: Account) => Promise<void>;
  saveSettings: (settings: TaxSettings) => Promise<void>;
  /** First-run go-live: create the org, chart, first entity, settings + owner. */
  completeSetup: (input: SetupInput) => Promise<void>;
  reset: () => Promise<void>;

  /** Authenticate against a local user; returns true on success. */
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  /** Owner-only member management. */
  inviteMember: (input: InviteInput) => Promise<void>;
  updateMemberRole: (id: string, role: Role) => Promise<void>;
  removeMember: (id: string) => Promise<void>;
}

/** Derive a stable, URL-safe entity id from its name. */
function entityIdFrom(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "entity-1"
  );
}

/**
 * Choose the backing store. The server-backed API (Cloudflare Functions + D1) is
 * the default and enforces auth/RBAC/tenant isolation on the server. IndexedDB
 * remains a dev-only fallback for running the SPA without the Functions API
 * (plain `vite dev`); opt into the real API in dev with `VITE_API=1`.
 */
function createDefaultRepository(): Repository {
  const useApi = import.meta.env.PROD || import.meta.env.VITE_API === "1";
  return useApi ? new ApiRepository() : new IndexedDbRepository();
}

let repository: Repository = createDefaultRepository();

/** Swap the backing repository (used by tests). */
export function configureRepository(repo: Repository): void {
  repository = repo;
}

async function fetchAll(): Promise<
  Pick<AppState, "entities" | "accounts" | "entries" | "settings" | "meta" | "users" | "session">
> {
  const [entities, accounts, entries, settings, meta, users, session] = await Promise.all([
    repository.getEntities(),
    repository.getAccounts(),
    repository.getEntries(),
    repository.getSettings(),
    repository.getMeta(),
    repository.getUsers(),
    repository.getSession(),
  ]);
  return { entities, accounts, entries, settings, meta, users, session };
}

/** The signed-in user resolved from the current session, or null. */
export function selectCurrentUser(state: AppState): User | null {
  if (!state.session) return null;
  return state.users.find((u) => u.id === state.session?.userId) ?? null;
}

/** The signed-in user's role, or null when signed out. */
export function selectCurrentRole(state: AppState): Role | null {
  return selectCurrentUser(state)?.role ?? null;
}

export const useAppStore = create<AppState>((set, get) => {
  /** Guard a protected write; throws so an unauthorized call can never persist. */
  function authorize(action: Action): void {
    if (!can(selectCurrentRole(get()), action)) {
      throw new Error("You don't have permission to perform this action.");
    }
  }

  return {
    engine: { name: ENGINE_NAME, version: ENGINE_VERSION },
    status: "idle",
    error: null,
    entities: [],
    accounts: [],
    entries: [],
    settings: null,
    meta: null,
    users: [],
    session: null,
    scope: "consolidated",

    async load() {
      set({ status: "loading", error: null });
      try {
        await repository.init();
        const data = await fetchAll();
        // Drop an expired session so the gate re-shows and stale tokens don't linger.
        if (data.session && !sessionValid(data.session, Date.now())) {
          await repository.saveSession(null);
          set({ ...data, session: null, status: "ready" });
        } else {
          set({ ...data, status: "ready" });
        }
      } catch (err) {
        set({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load ledger data.",
        });
      }
    },

    setScope(scope) {
      set({ scope });
    },

    async addEntry(entry) {
      authorize("entry.post");
      await repository.saveEntry(entry);
      set((s) => ({ entries: [...s.entries, entry] }));
    },

    async updateEntry(entry) {
      authorize("entry.post");
      // saveEntry is an upsert keyed on id, so this amends the existing entry.
      await repository.saveEntry(entry);
      set((s) => ({
        entries: s.entries.some((e) => e.id === entry.id)
          ? s.entries.map((e) => (e.id === entry.id ? entry : e))
          : [...s.entries, entry],
      }));
    },

    async deleteEntry(id) {
      authorize("entry.post");
      await repository.deleteEntry(id);
      set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
    },

    async saveAccount(account) {
      authorize("account.add");
      await repository.saveAccount(account);
      set((s) => ({
        accounts: s.accounts.some((a) => a.code === account.code)
          ? s.accounts.map((a) => (a.code === account.code ? account : a))
          : [...s.accounts, account],
      }));
    },

    async saveSettings(settings) {
      authorize("settings.edit");
      await repository.saveSettings(settings);
      set({ settings });
    },

    async completeSetup(input) {
      set({ status: "loading", error: null });
      try {
        // Server-enforced setup: create the org + owner atomically on the server
        // (which also signs the owner in), then re-fetch the fresh tenant state.
        if (isAuthRepository(repository)) {
          await repository.completeSetup({
            orgName: input.orgName,
            baseCurrency: input.baseCurrency,
            fiscalYearStartMonth: input.fiscalYearStartMonth,
            entityName: input.entityName,
            entityKind: input.entityKind,
            ownerName: input.ownerName,
            ownerEmail: input.ownerEmail,
            password: input.password,
          });
          set({ ...(await fetchAll()), status: "ready" });
          return;
        }
        const settings = buildInitialSettings(input.baseCurrency);
        const orgId = orgIdFrom(input.orgName);
        const entity: Entity = {
          id: entityIdFrom(input.entityName),
          name: input.entityName.trim(),
          kind: input.entityKind,
          baseCurrency: input.baseCurrency,
        };
        const meta: AppMeta = {
          setupComplete: true,
          orgId,
          orgName: input.orgName.trim(),
          fiscalYearStartMonth: input.fiscalYearStartMonth,
        };
        // Seed the first-run user as the organization owner (identity mandate).
        const owner: User = {
          id: newUserId(),
          orgId,
          name: input.ownerName.trim(),
          email: normalizeEmail(input.ownerEmail),
          role: "owner",
          passwordHash: await hashPassword(input.password),
          createdAt: Date.now(),
        };
        // Give the fresh tenant a usable ledger: default chart, settings, first
        // entity, then stamp setup complete so the wizard never runs again.
        for (const account of DEFAULT_CHART) await repository.saveAccount(account);
        await repository.saveSettings(settings);
        await repository.saveEntity(entity);
        await repository.saveMeta(meta);
        await repository.saveUser(owner);
        // Sign the owner in immediately so setup flows straight into the app.
        const now = Date.now();
        const session: Session = {
          userId: owner.id,
          orgId,
          token: newToken(),
          createdAt: now,
          expiresAt: now + SESSION_TTL_MS,
        };
        await repository.saveSession(session);
        set({ ...(await fetchAll()), status: "ready" });
      } catch (err) {
        set({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to complete setup.",
        });
      }
    },

    async reset() {
      authorize("data.reset");
      set({ status: "loading", error: null });
      try {
        await repository.reset();
        set({ ...(await fetchAll()), status: "ready" });
      } catch (err) {
        set({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to reset ledger data.",
        });
      }
    },

    async signIn(email, password) {
      // Server-enforced auth: the server verifies credentials and sets the
      // HttpOnly session cookie; re-fetch so meta/users/session reflect it.
      if (isAuthRepository(repository)) {
        const session = await repository.signIn(email, password);
        if (!session) return false;
        set({ ...(await fetchAll()) });
        return true;
      }
      // Local-first fallback (IndexedDB dev build / in-memory tests): verify the
      // stored PBKDF2 verifier in the browser and persist a local session.
      const target = normalizeEmail(email);
      // Read fresh from the repo in case the gate mounts before load() finished.
      const users = get().users.length ? get().users : await repository.getUsers();
      const user = users.find((u) => normalizeEmail(u.email) === target);
      if (!user || !(await verifyPassword(password, user.passwordHash))) {
        return false;
      }
      const now = Date.now();
      const session: Session = {
        userId: user.id,
        orgId: user.orgId,
        token: newToken(),
        createdAt: now,
        expiresAt: now + SESSION_TTL_MS,
      };
      await repository.saveSession(session);
      set({ users, session });
      return true;
    },

    async signOut() {
      await repository.saveSession(null);
      set({ session: null });
    },

    async inviteMember({ name, email, role, password }) {
      authorize("members.manage");
      const emailNorm = normalizeEmail(email);
      if (get().users.some((u) => normalizeEmail(u.email) === emailNorm)) {
        throw new Error("A member with that email already exists.");
      }
      const user: User = {
        id: newUserId(),
        orgId: get().meta?.orgId ?? "",
        name: name.trim(),
        email: emailNorm,
        role,
        passwordHash: await hashPassword(password),
        createdAt: Date.now(),
      };
      await repository.saveUser(user);
      set((s) => ({ users: [...s.users, user] }));
    },

    async updateMemberRole(id, role) {
      authorize("members.manage");
      const users = get().users;
      const target = users.find((u) => u.id === id);
      if (!target || target.role === role) return;
      if (
        target.role === "owner" &&
        role !== "owner" &&
        users.filter((u) => u.role === "owner").length <= 1
      ) {
        throw new Error("The organization must keep at least one owner.");
      }
      const updated: User = { ...target, role };
      await repository.saveUser(updated);
      set((s) => ({ users: s.users.map((u) => (u.id === id ? updated : u)) }));
    },

    async removeMember(id) {
      authorize("members.manage");
      const state = get();
      const target = state.users.find((u) => u.id === id);
      if (!target) return;
      if (target.id === state.session?.userId) {
        throw new Error("You can't remove your own account.");
      }
      if (
        target.role === "owner" &&
        state.users.filter((u) => u.role === "owner").length <= 1
      ) {
        throw new Error("The organization must keep at least one owner.");
      }
      await repository.deleteUser(id);
      set((s) => ({ users: s.users.filter((u) => u.id !== id) }));
    },
  };
});

/** Scope helper: the entity id to filter by, or undefined for consolidated. */
export function scopeEntityId(scope: Scope): string | undefined {
  return scope === "consolidated" ? undefined : scope;
}
